// ============================================================================
// VersionToast — "new version available, reload" prompt.
//
// Why this exists: there is no service worker / PWA, and firebase.json already
// serves index.html as no-cache with /assets/** immutable. That's correct for
// a *fresh* page load, but a tab that stays open across a deploy never
// re-fetches index.html, so it keeps running stale JS that points at hashed
// asset files which may no longer exist. (Trello-cutover 2026-05-29 #3.)
//
// Fix: vite stamps a build id into the bundle (__APP_BUILD_ID__) and writes the
// same id to /version.json. This component polls /version.json with
// cache:'no-store' (+ a cache-busting query param so we never depend on
// hosting cache headers) and shows a non-blocking banner with a Reload action
// when the deployed build id differs from the one this tab booted with.
//
// Dev note: /version.json is only emitted by `vite build`, so in `vite dev`
// the fetch 404s and we simply never prompt — intended.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

const POLL_INTERVAL_MS = 3 * 60 * 1000; // every 3 minutes

async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { buildId?: string };
    return typeof json.buildId === 'string' ? json.buildId : null;
  } catch {
    // Offline / transient — try again on the next tick.
    return null;
  }
}

export function VersionToast() {
  const [stale, setStale] = useState(false);
  // The build id this tab booted with. `typeof` guard keeps it safe if the
  // define was somehow not applied (e.g. an unusual test harness).
  const bootId = useRef(
    typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : null,
  );

  useEffect(() => {
    if (!bootId.current) return; // nothing to compare against
    let active = true;

    const check = async () => {
      const deployed = await fetchDeployedBuildId();
      if (!active || !deployed) return;
      if (deployed !== bootId.current) setStale(true);
    };

    // Check on mount, when the tab regains focus, and on an interval.
    void check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!stale) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-4 z-[110] flex justify-center px-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2"
    >
      <div className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-lg border border-tcs-border bg-tcs-inverse px-4 py-3 text-sm text-tcs-inverse-ink shadow-lg">
        <RefreshCw className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 leading-tight">
          A new version of the TCS App is available.
        </span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="shrink-0 rounded-md bg-tcs-inverse-ink px-3 py-1.5 text-xs font-semibold text-tcs-inverse hover:opacity-90"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => setStale(false)}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 text-tcs-inverse-ink/70 hover:text-tcs-inverse-ink"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
